// src/pages/Home/components/SandHome.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import sandBaseImg from "../../../assets/image/home/sand.png";
import sandCloudImg from "../../../assets/image/home/sandcloud.png";
import sandPhoneImg from "../../../assets/image/home/sandPhone.svg";

const SandHome = () => {
    const titleLines = [
        "wizMarket, AI 기술을 활용해",
        "맞춤 이미지를 만들고",
        "마케팅 효과를 극대화해보세요",
    ];

    const [visibleLines, setVisibleLines] = useState(0);

    // 상태/제어용 ref들
    const accumRef = useRef(0);
    const lastTouchY = useRef(null);
    const isLockedRef = useRef(false);
    const introUntilRef = useRef(0);
    const inViewRef = useRef(false);
    const sectionRef = useRef(null);
    const visibleLinesRef = useRef(0);
    const tryStepRef = useRef(null);
    const autoKickTimeoutRef = useRef(null);
    const thresholdRef = useRef(160);

    useEffect(() => {
        visibleLinesRef.current = visibleLines;
    }, [visibleLines]);

    useEffect(() => {
        thresholdRef.current = visibleLines >= 2 ? 80 : visibleLines >= 1 ? 70 : 150;
    }, [visibleLines]);

    // 잠금 함수
    const lock = useCallback((ms) => {
        if (isLockedRef.current) return true;
        isLockedRef.current = true;
        setTimeout(() => (isLockedRef.current = false), ms);
        return false;
    }, []);

    // 라인 드롭 시도
    const tryStep = useCallback((dir) => {
        const now = Date.now();
        if (!inViewRef.current) return;              // 섹션 in-view 아닐 때 무시
        if (now < introUntilRef.current) return;     // 인트로 지연 중 무시
        if (isLockedRef.current) return;             // 쿨다운 중 무시

        let progressed = false;
        setVisibleLines((v) => {
            const total = titleLines.length;
            if (dir > 0 && v >= total) return v;
            if (dir < 0 && v <= 0) return v;

            // 두 번째 줄부터는 더 짧은 쿨다운
            const cooldown = dir >0
            ? (v >= 2 ? 100 : v >= 1 ? 80 : 80)
            :160;
            if (lock(cooldown)) return v;
            
            const next = v + (dir > 0 ? 1 : -1);
            progressed = next !== v;
            return Math.max(0, Math.min(next, total));
        });
        return progressed;
    }, [lock, titleLines.length]);

    useEffect(() => {
        tryStepRef.current = tryStep;
    }, [tryStep]);

    // 1) IntersectionObserver: 섹션 진입/이탈 감지 + 짧은 인트로 지연 + 자동 1줄 드롭
    useEffect(() => {
        const INTRO_LOCK_MS = 0;          // ⬅️ 진입 후 0.3초만 입력 잠금
        const AUTOKICK_DELAY_MS = 0;      // ⬅️ 0.3초 후 첫 줄 자동 드롭
        const target = sectionRef.current;
        if (!target) return;

        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target !== target) return;
                if (entry.isIntersecting) {
                    inViewRef.current = true;
                    introUntilRef.current = Date.now() + INTRO_LOCK_MS;

                    // 잔여 스크롤 초기화
                    accumRef.current = 0;
                    lastTouchY.current = null;

                    // 기존 타이머 클리어 후, 자동 1줄 드롭 예약
                    if (autoKickTimeoutRef.current) clearTimeout(autoKickTimeoutRef.current);
                    autoKickTimeoutRef.current = setTimeout(() => {
                        if (inViewRef.current && visibleLinesRef.current === 0) {
                            // tryStepRef.current?.(1);
                            requestAnimationFrame(() => tryStepRef.current?.(1)); // 즉시 드롭
                        }
                    }, AUTOKICK_DELAY_MS);
                } else {
                    inViewRef.current = false;
                    accumRef.current = 0;
                    lastTouchY.current = null;
                    isLockedRef.current = false;
                    // 완전히 화면 밖으로 나갔을 때만 리셋(재진입 시 처음부터 재생)
                    if (entry.intersectionRatio === 0) {
                        setVisibleLines(0);
                        visibleLinesRef.current = 0;
                    }
                    if (autoKickTimeoutRef.current) {
                        clearTimeout(autoKickTimeoutRef.current);
                        autoKickTimeoutRef.current = null;
                    }
                }
            });
        }, { 
            threshold: 0.25,
            rootMargin: "0px 0px -20% 0px", 
        });

        io.observe(target);
        return () => {
            io.unobserve(target);
            io.disconnect();
            if (autoKickTimeoutRef.current) {
                clearTimeout(autoKickTimeoutRef.current);
                autoKickTimeoutRef.current = null;
            }
        };
    }, []);

    // 2) 입력 리스너: inView일 때만 동작
    useEffect(() => {
        // const THRESH = 160;
        const target = sectionRef.current;
        if (!target) return;

        const onWheel = (e) => {
            if (!inViewRef.current) return;
            const THRESH = thresholdRef.current;
            accumRef.current += e.deltaY;
            if (accumRef.current > THRESH) {
                const ok = tryStep(1);
                if (ok) accumRef.current = 0;
            } else if (accumRef.current < -THRESH) {
                const ok = tryStep(-1);
                if (ok) accumRef.current = 0;                
            }
        };

        const onKey = (e) => {
            if (!inViewRef.current) return;
            if (e.repeat) return;
            if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") tryStep(1);
            if (e.key === "ArrowUp" || e.key === "PageUp") tryStep(-1);
        };

        const onTouchStart = (e) => {
            if (!inViewRef.current) return;
            lastTouchY.current = e.touches[0].clientY;
        };

        const onTouchMove = (e) => {
            if (!inViewRef.current) return;
            if (lastTouchY.current == null) return;
            const THRESH = thresholdRef.current;
            const dy = lastTouchY.current - e.touches[0].clientY; // 아래 스크롤 시 양수
            accumRef.current += dy;
            if (accumRef.current > THRESH) {
                const ok = tryStep(1);
                if (ok) accumRef.current = 0;
            } else if (accumRef.current < -THRESH) {
                const ok = tryStep(-1);
                if (ok) accumRef.current = 0;
            }
            lastTouchY.current = e.touches[0].clientY;
        };

        const onTouchEnd = () => {
            if (!inViewRef.current) return;
            lastTouchY.current = null;
        };

        // 바인딩
        window.addEventListener("wheel", onWheel, { passive: true });
        target.addEventListener("touchstart", onTouchStart, { passive: true });
        target.addEventListener("touchmove", onTouchMove, { passive: true });
        target.addEventListener("touchend", onTouchEnd, { passive: true });
        window.addEventListener("keydown", onKey);

        // 클린업
        return () => {
            window.removeEventListener("wheel", onWheel);
            target.removeEventListener("touchstart", onTouchStart);
            target.removeEventListener("touchmove", onTouchMove);
            target.removeEventListener("touchend", onTouchEnd);
            window.removeEventListener("keydown", onKey);
        };
    }, [tryStep]);

    // 줄 낙하 애니메이션
    const lineDrop = {
        initial: { opacity: 0, y: -56, filter: "blur(6px)" },
        animate: {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        },
        exit: {
            opacity: 0,
            y: -24,
            filter: "blur(3px)",
            transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
        },
    };

    // 컨테이너 페이드업
    const container = {
        hidden: { opacity: 0, y: -8 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
        },
    };

    return (
        <motion.section
            ref={sectionRef}
            className="relative isolate overflow-hidden bg-[#050505] py-10"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.01 }}
            style={{
                "--phone-w": "clamp(220px, 16.02vw, 380px)",
                "--phone-h": "calc(var(--phone-w) * 2.15)",
            }}
        >
            {/* 메인 컨테이너 */}
            <div
                className="relative max-w-7xl mx-auto px-6 py-16 md:py-24"
                style={{ minHeight: "calc(var(--phone-h) + 12vh)" }}
            >
                <div className="grid grid-cols-12 items-center gap-x-8 gap-y-10">
                    {/* 왼쪽 텍스트 */}
                    <div className="z-50 col-span-12 sm:col-span-6 lg:col-span-7 text-left">
                        <h2 className="break-keep font-extrabold tracking-tight leading-[1.1] text-2xl sm:text-3xl md:text-4xl lg:text-5xl space-y-6">
                            <AnimatePresence initial={false} mode="popLayout">
                                {titleLines.slice(0, visibleLines).map((line, idx) => (
                                    <motion.span
                                        key={idx}
                                        variants={lineDrop}
                                        initial="initial"
                                        animate="animate"
                                        exit="exit"
                                        className="block"
                                    >
                                        {line}
                                    </motion.span>
                                ))}
                            </AnimatePresence>
                        </h2>

                        {/* 모든 줄이 내려온 후 본문 표시 */}
                        <AnimatePresence initial={false}>
                            {visibleLines >= titleLines.length && (
                                <motion.p
                                    key="desc"
                                    initial={{ opacity: 0, y: -24, filter: "blur(4px)" }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                        filter: "blur(0px)",
                                        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
                                    }}
                                    exit={{ opacity: 0, y: -12 }}
                                    className="break-keep mt-6 text-gray-300 leading-relaxed text-base sm:text-lg max-w-[42rem]"
                                >
                                    AI 기반의 맞춤형 이미지를 통해 다양한 스타일의 마케팅 콘텐츠를
                                    빠르게 만들 수 있어요. <br />
                                    트렌드에 맞는 비주얼로 더 많은 사람들의 관심을 끌고,
                                    효과적으로 홍보할 수 있어요.
                                </motion.p>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* 오른쪽 폰 (컬럼 하단 정렬, 비율 유지) */}
                    <div
                        className="col-span-12 sm:col-span-6 lg:col-span-5 lg:pr-[3%] flex justify-center lg:justify-end items-end"
                        style={{ minHeight: "var(--phone-h)" }}
                    >
                        <AnimatePresence initial={false}>
                            {visibleLines > 0 && (
                                <motion.img
                                    src={sandPhoneImg}
                                    alt="앱 화면 미리보기"
                                    variants={lineDrop}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    className="z-30 drop-shadow-2xl"
                                    style={{ width: "var(--phone-w)", height: "auto" }}
                                    loading="lazy"
                                    decoding="async"
                                />
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            {/* 모래 FX : 폰 너비 × 스케일, 비율 유지 */}
            <div
                className="pointer-events-none absolute inset-x-0 bottom-0
                           [--sand-scale:7] sm:[--sand-scale:8.5] lg:[--sand-scale:10.5] xl:[--sand-scale:12]"
            >
                {/* 뒤 모래 */}
                <img
                    src={sandBaseImg}
                    alt=""
                    aria-hidden="true"
                    className="absolute bottom-0 -z-10 left-1/2 -translate-x-1/2 select-none"
                    style={{
                        width: "calc(var(--phone-w) * var(--sand-scale))",
                        height: "auto",
                        WebkitMaskImage: "linear-gradient(to top, black 76%, transparent 100%)",
                        maskImage: "linear-gradient(to top, black 76%, transparent 100%)",
                    }}
                    loading="eager"
                    decoding="async"
                />

                {/* 앞 모래 */}
                <img
                    src={sandCloudImg}
                    alt=""
                    aria-hidden="true"
                    className="absolute bottom-0 z-40 left-1/2 -translate-x-1/2 select-none"
                    style={{
                        width: "calc(var(--phone-w) * var(--sand-scale))",
                        height: "auto",
                        WebkitMaskImage: "linear-gradient(to top, black 50%, transparent 88%)",
                        maskImage: "linear-gradient(to top, black 50%, transparent 88%)",
                    }}
                    loading="lazy"
                    decoding="async"
                />
            </div>
        </motion.section>
    );
};

export default SandHome;
